<template>
  <v-card
    v-if="showUploadStatus"
    color="primary"
    elevation="0"
    class="mx-3 mb-3"
  >
    <v-card-text class="pa-3">
      <div class="d-flex flex-column">
        <div class="d-flex justify-space-between align-center mb-2">
          <span class="font-weight-bold white--text">
            <v-icon small class="mr-2" color="white">mdi-cloud-upload</v-icon>
            Bestanden uploaden
          </span>
          <v-btn icon x-small @click="closeStatus" color="white">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="stats.uploading > 0 || stats.pending > 0" class="mb-2">
          <div class="text-caption mb-1 white--text">
            {{ stats.uploading + stats.pending }} bestand{{ stats.uploading + stats.pending > 1 ? 'en' : '' }} bezig...
          </div>
          <v-progress-linear
            :value="overallProgress"
            color="white"
            background-color="rgba(255,255,255,0.3)"
            height="6"
            rounded
          ></v-progress-linear>
        </div>

        <div v-if="stats.completed > 0" class="text-caption white--text">
          <v-icon small color="success">mdi-check-circle</v-icon>
          {{ stats.completed }} voltooid
        </div>

        <div v-if="stats.failed > 0" class="text-caption white--text mt-1">
          <v-icon small color="error">mdi-alert-circle</v-icon>
          {{ stats.failed }} mislukt
          <v-btn
            x-small
            text
            color="white"
            @click="retryFailed"
            class="ml-1 pa-0"
            style="text-transform: none;"
          >
            Opnieuw proberen
          </v-btn>
        </div>

        <div v-if="queuedFiles.length > 0" class="mt-2">
          <v-divider dark class="mb-2"></v-divider>
          <div class="text-caption white--text mb-1">In wachtrij:</div>
          <div class="file-list-container">
            <div
              v-for="file in queuedFiles"
              :key="file.id"
              class="d-flex align-center mb-1"
            >
              <div
                v-if="isImage(file.fileName)"
                class="file-thumbnail mr-2"
                :style="{ backgroundImage: `url(${file.fileData})` }"
              ></div>
              <v-icon
                v-else
                small
                color="white"
                class="mr-2"
              >
                {{ getFileIcon(file.fileName) }}
              </v-icon>
              <div class="text-caption text-truncate white--text flex-grow-1">
                {{ file.fileName }}
              </div>
              <v-icon
                v-if="file.uploadStatus === 'uploading'"
                small
                color="white"
                class="rotating"
              >
                mdi-loading
              </v-icon>
              <v-chip
                v-else-if="file.uploadStatus === 'failed'"
                x-small
                color="error"
                class="ml-1"
              >
                Fout
              </v-chip>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { EventBus } from '@/utils/eventBus'
import fileUploadService from '@/services/fileUploadService'
import offlineStorage from '@/utils/offlineStorage'

export default {
  name: 'FileUploadStatus',
  data () {
    return {
      showUploadStatus: false,
      stats: {
        pending: 0,
        uploading: 0,
        completed: 0,
        failed: 0
      },
      queuedFiles: [],
      autoHideTimeout: null
    }
  },
  computed: {
    overallProgress () {
      const total = this.stats.pending + this.stats.uploading + this.stats.completed + this.stats.failed
      if (total === 0) return 0
      return Math.round((this.stats.completed / total) * 100)
    }
  },
  mounted () {
    // Listen for upload events
    EventBus.$on('file-queued', this.handleFileQueued)
    EventBus.$on('file-upload-started', this.handleUploadStarted)
    EventBus.$on('file-uploaded', this.handleFileUploaded)
    EventBus.$on('file-upload-failed', this.handleUploadFailed)
    EventBus.$on('upload-processing-started', this.handleProcessingStarted)
    EventBus.$on('upload-processing-completed', this.handleProcessingCompleted)

    // Update stats on mount
    this.updateStats()
  },
  beforeDestroy () {
    // Clean up event listeners
    EventBus.$off('file-queued', this.handleFileQueued)
    EventBus.$off('file-upload-started', this.handleUploadStarted)
    EventBus.$off('file-uploaded', this.handleFileUploaded)
    EventBus.$off('file-upload-failed', this.handleUploadFailed)
    EventBus.$off('upload-processing-started', this.handleProcessingStarted)
    EventBus.$off('upload-processing-completed', this.handleProcessingCompleted)
  },
  methods: {
    async updateStats () {
      this.stats = await fileUploadService.getUploadStats()
      await this.updateQueuedFiles()
    },
    async updateQueuedFiles () {
      // Get all pending, uploading, and failed uploads
      const pending = await offlineStorage.getFileUploadsByStatus('pending')
      const uploading = await offlineStorage.getFileUploadsByStatus('uploading')
      const failed = await offlineStorage.getFileUploadsByStatus('failed')

      this.queuedFiles = [...pending, ...uploading, ...failed]
        .sort((a, b) => a.timestamp - b.timestamp)
    },
    isImage (fileName) {
      if (!fileName) return false
      const ext = fileName.split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
    },
    getFileIcon (fileName) {
      if (!fileName) return 'mdi-file'
      const ext = fileName.split('.').pop().toLowerCase()

      if (['pdf'].includes(ext)) return 'mdi-file-pdf-box'
      if (['doc', 'docx'].includes(ext)) return 'mdi-file-word'
      if (['xls', 'xlsx'].includes(ext)) return 'mdi-file-excel'
      if (['zip', 'rar', '7z'].includes(ext)) return 'mdi-folder-zip'
      if (['txt'].includes(ext)) return 'mdi-file-document'

      return 'mdi-file'
    },
    handleFileQueued ({ fileName }) {
      this.showUploadStatus = true
      this.updateStats()
      this.clearAutoHide()
    },
    handleUploadStarted ({ fileName, id }) {
      this.updateStats()
    },
    handleFileUploaded ({ fileName, id }) {
      this.updateStats()
    },
    handleUploadFailed ({ fileName, id }) {
      this.updateStats()
    },
    handleProcessingStarted () {
      this.showUploadStatus = true
      this.clearAutoHide()
      this.updateStats()
    },
    handleProcessingCompleted ({ total, success, failed }) {
      this.updateStats()

      // Auto-hide after 5 seconds if all done
      if (this.stats.pending === 0 && this.stats.uploading === 0) {
        this.autoHideTimeout = setTimeout(async () => {
          await fileUploadService.clearCompletedUploads()
          this.showUploadStatus = false
        }, 5000)
      }
    },
    clearAutoHide () {
      if (this.autoHideTimeout) {
        clearTimeout(this.autoHideTimeout)
        this.autoHideTimeout = null
      }
    },
    closeStatus () {
      fileUploadService.clearCompletedUploads()
      this.showUploadStatus = false
    },
    async retryFailed () {
      await fileUploadService.retryFailedUploads()
      this.updateStats()
    }
  }
}
</script>

<style scoped>
.text-truncate {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-list-container {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.file-list-container::-webkit-scrollbar {
  width: 6px;
}

.file-list-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.file-list-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.file-list-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.file-thumbnail {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 4px;
  background-size: cover;
  background-position: center;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.rotating {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
